import React, { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/shadcn/accordion';
import { homeLessonDoneToday, REPEAT_COPY } from '../Sparks/earnedToday';
import LessonRate from '../Sparks/LessonRate';
import { groupLessonsByYear, lessonsForChild, progressFor } from './lessonGroups';
import ProgressStones from './ProgressStones';
import { playOpen, playSelect } from '../../utils/uiSounds';

// Topic blocks cycle through these. Each pairing keeps text contrast at or above WCAG AA.
const TOPIC_COLOURS = [
  'bg-violet-600 text-white',
  'bg-sky-600 text-white',
  'bg-emerald-600 text-white',
  'bg-amber-400 text-amber-950',
  'bg-pink-600 text-white',
  'bg-teal-600 text-white',
];

function LessonCard({ lesson, earnedToday, homeRates, ratesOn, onStart }) {
  const lessonId = lesson.lessonId._id;
  const doneToday = homeLessonDoneToday(earnedToday, lessonId);
  return (
    <li className="flex flex-col gap-3 rounded-2xl border-2 border-gray-100 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md motion-reduce:transform-none md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        <span className="text-base font-bold text-foreground">{lesson.lessonId.title}</span>
        {doneToday && (
          <span className="ml-2 inline-flex flex-wrap items-center gap-1 align-middle" data-testid="lesson-done-today">
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">{REPEAT_COPY.doneToday}</span>
            <span className="text-xs font-normal text-gray-500">{REPEAT_COPY.doneTodayHint}</span>
          </span>
        )}
        {earnedToday?.homeLearningOncePerDay && !doneToday && !ratesOn && (
          <span className="ml-2 rounded-full bg-[#F0EDFA] px-2 py-0.5 align-middle text-xs font-semibold text-[#5C3BD1]" data-testid="lesson-earns">
            {REPEAT_COPY.earns}
          </span>
        )}
        {ratesOn && <LessonRate rate={homeRates.lessons[lessonId]} showTag={!doneToday} />}
      </div>
      <button
        type="button"
        onClick={() => onStart(lessonId)}
        className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 font-bold text-white shadow transition-all duration-200 hover:scale-105 hover:bg-indigo-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300 active:scale-95 motion-reduce:transform-none max-md:w-full"
      >
        <Play className="h-4 w-4 fill-current" aria-hidden="true" />
        Let's go!
      </button>
    </li>
  );
}

/**
 * A list of topic blocks. Picking one highlights it, slides the list into a side rail and reveals
 * that topic's lessons grouped into collapsible year groups (the child's own year opens first). With Home Learning
 * rates on, each topic block and year group shows its lessons' colours as stepping stones.
 */
export default function TopicBrowser({ topics, childYearGroup, emptyMessage, earnedToday, homeRates, ratesOn, onStartLesson, onTopicSelect }) {
  const rates = ratesOn ? homeRates.lessons : null;
  const [selectedId, setSelectedId] = useState(null);
  const lessonsRef = useRef(null);
  const selected = topics.find((topic) => topic._id === selectedId) ?? null;
  const yearGroups = selected ? groupLessonsByYear(selected) : [];
  const [openYears, setOpenYears] = useState([]);

  useEffect(() => {
    if (!selected) return;
    const years = groupLessonsByYear(selected).map((group) => group.yearGroup);
    // Open the child's own year; with a single group there is nothing to choose, so open it.
    setOpenYears(years.includes(childYearGroup) ? [childYearGroup] : years.length === 1 ? years : []);
    // On phones the lessons sit below the topics, so bring them into view.
    if (window.matchMedia?.('(max-width: 767px)').matches) {
      lessonsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [selected, childYearGroup]);

  const selectTopic = (topic) => {
    if (topic._id === selectedId) return;
    playSelect();
    setSelectedId(topic._id);
    onTopicSelect?.(topic);
  };

  const changeOpenYears = (next) => {
    if (next.length > openYears.length) playOpen();
    setOpenYears(next);
  };

  if (!topics.length) {
    return <p className="rounded-2xl bg-gray-50 p-6 text-center text-gray-600">{emptyMessage}</p>;
  }

  return (
    <div className={`flex flex-col gap-6 ${selected ? 'md:flex-row md:items-start' : ''}`}>
      <ul
        className={`grid gap-3 transition-all duration-500 motion-reduce:transition-none ${
          selected ? 'grid-cols-2 md:w-64 md:shrink-0 md:grid-cols-1' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
        }`}
        aria-label="Topics"
      >
        {topics.map((topic, index) => {
          const isSelected = topic._id === selectedId;
          return (
            <li key={topic._id}>
              <button
                type="button"
                onClick={() => selectTopic(topic)}
                aria-pressed={isSelected}
                className={`w-full rounded-2xl text-left font-extrabold shadow-sm transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300 active:scale-95 motion-reduce:transform-none motion-reduce:transition-none ${
                  TOPIC_COLOURS[index % TOPIC_COLOURS.length]
                } ${selected ? 'px-4 py-3 text-base' : 'min-h-[110px] p-5 text-lg hover:-translate-y-1 hover:shadow-lg'} ${
                  isSelected ? 'ring-4 ring-indigo-300 ring-offset-2 md:translate-x-2' : selected ? 'opacity-70 hover:opacity-100' : ''
                }`}
              >
                {topic.topic}
                <ProgressStones progress={progressFor(lessonsForChild(topic, childYearGroup), rates)} />
              </button>
            </li>
          );
        })}
      </ul>

      {selected && (
        <section
          key={selected._id}
          ref={lessonsRef}
          aria-label={`${selected.topic} lessons`}
          className="min-w-0 flex-1 scroll-mt-4 rounded-3xl bg-[#F1EEF7] p-4 animate-in fade-in slide-in-from-right-8 duration-500 motion-reduce:animate-none md:p-6"
        >
          <h2 className="mb-4 text-2xl font-extrabold text-[#2F1E6B]">{selected.topic}</h2>
          {yearGroups.length ? (
            <Accordion type="multiple" value={openYears} onValueChange={changeOpenYears} className="flex flex-col gap-3">
              {yearGroups.map(({ yearGroup, lessons }) => (
                <AccordionItem key={yearGroup} value={yearGroup} className="rounded-2xl border-b-0 bg-white px-4 shadow-sm">
                  <AccordionTrigger className="text-lg font-extrabold text-[#2F1E6B] hover:no-underline">
                    <span className="flex flex-wrap items-center gap-x-3">
                      {yearGroup}
                      <span className="rounded-full bg-[#F0EDFA] px-2.5 py-0.5 text-xs font-bold text-[#5C3BD1]">
                        {lessons.length} {lessons.length === 1 ? 'lesson' : 'lessons'}
                      </span>
                      <ProgressStones progress={progressFor(lessons, rates)} onLight />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="flex flex-col gap-3">
                      {lessons.map((lesson) => (
                        <LessonCard
                          key={lesson.lessonId._id}
                          lesson={lesson}
                          earnedToday={earnedToday}
                          homeRates={homeRates}
                          ratesOn={ratesOn}
                          onStart={(lessonId) => onStartLesson(selected._id, lessonId)}
                        />
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <p className="text-gray-600">No lessons here yet. Check back soon!</p>
          )}
        </section>
      )}
    </div>
  );
}
